import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import { Plus, Trash2, Save, CheckCircle2, ArrowLeft, AlertCircle, Wrench, PackagePlus } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { StatusPill } from '@/components/StatusPill';
import { Timeline } from '@/components/Timeline';
import { RelatedDocsPanel } from '@/components/RelatedDocsPanel';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { money, humanizeEnum } from '@/lib/format';
import { toast } from '@/components/Toaster';

const WO_TYPES: { value: string; label: string }[] = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'rebuild',     label: 'Rebuild / Refurbish' },
  { value: 'warranty',    label: 'Warranty' },
  { value: 'general',     label: 'General' },
  { value: 'pcr',         label: 'PCR — Program Component Replacement' },
];

const WO_SUBTYPES: Record<string, { value: string; label: string }[]> = {
  maintenance: [
    { value: 'planned',   label: 'Planned' },
    { value: 'unplanned', label: 'Unplanned' },
  ],
  general: [
    { value: 'labour',   label: 'Labour (Training)' },
    { value: 'material', label: 'Material (General activity)' },
  ],
};

// The requisition links to a unit asset for maintenance, and to a component
// asset for rebuild/warranty/PCR — surface that in the field label.
function assetLabelForType(woType: string): string {
  switch (woType) {
    case 'rebuild':
    case 'warranty':
    case 'pcr':
      return 'Component (Asset)';
    case 'maintenance':
      return 'Unit (Asset)';
    default:
      return 'Asset';
  }
}

interface Asset      { id: string; name: string; asset_name: string }
interface Project    { id: string; name: string; project_name: string }
interface MaintReq   { id: string; name: string; asset_name?: string }
interface Item       { id: string; code: string; name: string; stock_uom: string; standard_rate: string }
interface Warehouse  { id: string; code: string; name: string; is_group?: boolean }

interface WOPart {
  id: string; row_index: number; item_id: string;
  item_code?: string; item_name?: string;
  qty: string; warehouse_id?: string; rate: string; amount: string;
}
interface AssetWorkOrder {
  id: string; name: string; company_id: string;
  wo_type: string; wo_subtype?: string;
  requisition_status: string;
  maintenance_request_id?: string;
  asset_id: string; asset_name?: string;
  project_id?: string;
  status: string;
  planned_start?: string; planned_end?: string;
  labour_hours: string; labour_cost: string;
  total_parts_cost: string; total_cost: string;
  remarks?: string;
  parts: WOPart[];
}

interface DraftPart {
  rowId: string;
  item_id: string;
  item_code: string;
  item_name: string;
  qty: string;
  warehouse_id: string;
  rate: string;
}

const rid = () => Math.random().toString(36).slice(2);
const emptyPart = (): DraftPart => ({ rowId: rid(), item_id: '', item_code: '', item_name: '', qty: '1', warehouse_id: '', rate: '0' });

export function AssetWorkOrderForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['asset-work-order', id],
    enabled: !isNew,
    queryFn: () => api<AssetWorkOrder>(`/maintenance/asset-work-orders/${id}`),
  });
  const { data: assetsResp }     = useQuery({ queryKey: ['assets'],               queryFn: () => api<{ items: Asset[] }>('/assets/assets') });
  const { data: projectsResp }   = useQuery({ queryKey: ['projects'],             queryFn: () => api<{ items: Project[] }>('/projects/projects') });
  const { data: mreqResp }       = useQuery({ queryKey: ['maintenance-requests'], queryFn: () => api<{ items: MaintReq[] }>('/maintenance/maintenance-requests') });
  const { data: itemsResp }      = useQuery({ queryKey: ['items'],                queryFn: () => api<{ items: Item[] }>('/accounting/items') });
  const { data: warehousesResp } = useQuery({ queryKey: ['warehouses'],           queryFn: () => api<{ items: Warehouse[] }>('/stock/warehouses') });

  const assets     = assetsResp?.items ?? [];
  const projects   = projectsResp?.items ?? [];
  const mreqs      = mreqResp?.items ?? [];
  const items      = itemsResp?.items ?? [];
  const warehouses = useMemo(() => (warehousesResp?.items ?? []).filter((w) => !w.is_group), [warehousesResp]);

  const [woType, setWoType]         = useState('maintenance');
  const [woSubtype, setWoSubtype]   = useState('');
  const [maintenanceRequestID, setMaintenanceRequestID] = useState('');
  const [assetID, setAssetID]       = useState('');
  const [projectID, setProjectID]   = useState('');
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedEnd, setPlannedEnd]     = useState('');
  const [labourHours, setLabourHours]   = useState('0');
  const [labourCost, setLabourCost]     = useState('0');
  const [remarks, setRemarks]           = useState('');
  const [lines, setLines]               = useState<DraftPart[]>([emptyPart()]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setWoType(existing.wo_type ?? 'maintenance');
    setWoSubtype(existing.wo_subtype ?? '');
    setMaintenanceRequestID(existing.maintenance_request_id ?? '');
    setAssetID(existing.asset_id);
    setProjectID(existing.project_id ?? '');
    setPlannedStart((existing.planned_start ?? '').slice(0, 10));
    setPlannedEnd((existing.planned_end ?? '').slice(0, 10));
    setLabourHours(existing.labour_hours);
    setLabourCost(existing.labour_cost);
    setRemarks(existing.remarks ?? '');
    setLines((existing.parts ?? []).map((p) => ({
      rowId: p.id,
      item_id: p.item_id,
      item_code: p.item_code ?? '',
      item_name: p.item_name ?? '',
      qty: p.qty,
      warehouse_id: p.warehouse_id ?? '',
      rate: p.rate,
    })));
    if ((existing.parts ?? []).length === 0) setLines([]);
  }, [existing]);

  const completed = existing?.status === 'completed';
  const cancelled = existing?.status === 'cancelled';
  const editable  = isNew || (!completed && !cancelled);

  const assetOptions   = useMemo(() => assets.map((a) => ({ value: a.id, label: a.name, description: a.asset_name })), [assets]);
  const projectOptions = useMemo(() => projects.map((p) => ({ value: p.id, label: p.project_name, description: p.name })), [projects]);
  const mreqOptions    = useMemo(() => mreqs.map((m) => ({ value: m.id, label: m.name, description: m.asset_name })), [mreqs]);
  const itemOptions    = useMemo(() => items.map((it) => ({ value: it.id, label: it.name, description: it.code })), [items]);
  const warehouseOptions = useMemo(() => warehouses.map((w) => ({ value: w.id, label: w.name, description: w.code })), [warehouses]);

  const partsTotal = useMemo(() => lines.reduce(
    (a, l) => a.plus(new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'))),
    new Decimal(0),
  ), [lines]);
  const totalCost = partsTotal.plus(new Decimal(labourCost || '0'));

  function addLine() { setLines((s) => [...s, emptyPart()]); }
  function patch(rowId: string, p: Partial<DraftPart>) { setLines((s) => s.map((l) => l.rowId === rowId ? { ...l, ...p } : l)); }
  function remove(rowId: string) { setLines((s) => s.filter((l) => l.rowId !== rowId)); }
  function pickItem(rowId: string, itemID: string | null) {
    const it = items.find((i) => i.id === itemID);
    patch(rowId, {
      item_id: itemID ?? '',
      item_code: it?.code ?? '',
      item_name: it?.name ?? '',
      rate: it?.standard_rate ?? '0',
    });
  }

  function validate(): string[] {
    const out: string[] = [];
    if (!assetID) out.push('Asset is required.');
    lines.forEach((l, i) => {
      if (!l.item_id) { out.push(`Part ${i + 1}: pick an item.`); return; }
      if (new Decimal(l.qty || '0').lt(0)) out.push(`Part ${i + 1}: qty cannot be negative.`);
    });
    return out;
  }

  function buildBody() {
    return {
      wo_type: woType || undefined,
      wo_subtype: woSubtype || undefined,
      maintenance_request_id: maintenanceRequestID || undefined,
      asset_id: assetID,
      project_id: projectID || undefined,
      planned_start: plannedStart ? `${plannedStart}T00:00:00Z` : undefined,
      planned_end:   plannedEnd   ? `${plannedEnd}T00:00:00Z`   : undefined,
      labour_hours: labourHours || undefined,
      labour_cost:  labourCost || undefined,
      remarks: remarks || undefined,
      parts: lines.filter((l) => l.item_id).map((l) => ({
        item_id: l.item_id,
        qty: l.qty,
        warehouse_id: l.warehouse_id || undefined,
        rate: l.rate || undefined,
      })),
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<AssetWorkOrder>('/maintenance/asset-work-orders', { method: 'POST', body: buildBody() }),
    onSuccess: (awo) => {
      toast.success('Draft saved', awo.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/maintenance/asset-work-orders'] });
      navigate({ to: `/maintenance/asset-work-orders/${awo.id}` as never });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Save failed.'); toast.error('Could not save', e.message); },
  });

  const updateMutation = useMutation({
    mutationFn: () => api<AssetWorkOrder>(`/maintenance/asset-work-orders/${id}`, { method: 'PUT', body: buildBody() }),
    onSuccess: () => {
      toast.success('Saved', 'Work order updated.');
      qc.invalidateQueries({ queryKey: ['asset-work-order', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/maintenance/asset-work-orders'] });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Save failed.'); toast.error('Save failed', e.message); },
  });

  const completeMutation = useMutation({
    mutationFn: () => api<AssetWorkOrder>(`/maintenance/asset-work-orders/${id}/complete`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Completed', 'Cost rolled up.');
      qc.invalidateQueries({ queryKey: ['asset-work-order', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/maintenance/asset-work-orders'] });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Complete failed.'); toast.error('Complete failed', e.message); },
  });

  const submitProcessingMutation = useMutation({
    mutationFn: () => api<{ id: string; name: string }>(`/maintenance/asset-work-orders/${id}/submit-for-processing`, { method: 'POST', body: {} }),
    onSuccess: (mr) => {
      toast.success('Submitted for processing', `PMR ${mr.name} raised to supply chain.`);
      navigate({ to: `/accounting/material-requests/${mr.id}` as never });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Submit failed.'); toast.error('Could not submit for processing', e.message); },
  });

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }
  function onSave() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    updateMutation.mutate();
  }

  const title = isNew ? 'New Asset Work Order' : (existing?.name ?? '…');

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Asset Operations', to: '/maintenance' },
          { label: 'Asset Work Orders', to: '/maintenance/asset-work-orders' },
          { label: isNew ? 'New' : title },
        ]}
        title={title}
        status={existing && (
          <span className="inline-flex items-center gap-2">
            <StatusPill tone="accent" withDot={false}>{humanizeEnum(existing.wo_type)}{existing.wo_subtype ? ` · ${humanizeEnum(existing.wo_subtype)}` : ''}</StatusPill>
            <WorkOrderStatusPill value={existing.status} />
          </span>
        )}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/maintenance/asset-work-orders' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {isNew && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft <Kbd>⌘S</Kbd>
              </Button>
            )}
            {!isNew && editable && (
              <Button variant="secondary" onClick={onSave} loading={updateMutation.isPending}>
                <Save className="size-4" /> Save
              </Button>
            )}
            {!isNew && editable && (
              <Button onClick={() => completeMutation.mutate()} loading={completeMutation.isPending}>
                <CheckCircle2 className="size-4" /> Complete
              </Button>
            )}
            {!isNew && existing && existing.status !== 'cancelled' && (
              <Button variant="secondary" onClick={() => submitProcessingMutation.mutate()} loading={submitProcessingMutation.isPending}>
                <PackagePlus className="size-4" /> {existing.requisition_status === 'submitted' ? 'Re-submit for Processing' : 'Submit for Processing'}
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]"
      >
        <div className="space-y-4 min-w-0">
          {(completed || cancelled) && (
            <Card className="!p-3 flex items-center gap-3 bg-surface-soft border-hairline">
              <AlertCircle className="size-4 text-stone" />
              <span className="text-body text-text-secondary">
                This work order is {existing?.status} and can no longer be edited.
              </span>
            </Card>
          )}
          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          <Card>
            <CardTitle>Work order header</CardTitle>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="Work order type" hint={isNew ? 'Sets the document-number prefix (WO251–WO256).' : 'Type is fixed once created.'}>
                <Combobox
                  options={WO_TYPES}
                  value={woType || null}
                  onChange={(v) => { const nt = v ?? 'maintenance'; setWoType(nt); if (!(WO_SUBTYPES[nt] ?? []).some((s) => s.value === woSubtype)) setWoSubtype(''); }}
                  placeholder="Pick a type…"
                  disabled={!isNew || !editable}
                />
              </Field>
              {(WO_SUBTYPES[woType]?.length ?? 0) > 0 ? (
                <Field label="Subtype">
                  <Combobox
                    options={WO_SUBTYPES[woType] ?? []}
                    value={woSubtype || null}
                    onChange={(v) => setWoSubtype(v ?? '')}
                    placeholder="— None —"
                    clearable
                    disabled={!editable}
                  />
                </Field>
              ) : <div />}
              <Field label={assetLabelForType(woType)} hint="Required.">
                <Combobox options={assetOptions} value={assetID || null} onChange={(v) => setAssetID(v ?? '')} placeholder="Pick an asset…" disabled={!editable} />
              </Field>
              <Field label="Maintenance request" hint="Optional — the request that triggered this repair.">
                <Combobox options={mreqOptions} value={maintenanceRequestID || null} onChange={(v) => setMaintenanceRequestID(v ?? '')} placeholder="— None —" clearable disabled={!editable} />
              </Field>
              <Field label="Project (cost object)" hint="Optional.">
                <Combobox options={projectOptions} value={projectID || null} onChange={(v) => setProjectID(v ?? '')} placeholder="— None —" clearable disabled={!editable} />
              </Field>
              <div />
              <Field label="Planned start">
                <Input type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="Planned end">
                <Input type="date" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="Labour hours">
                <NumericInput value={labourHours} onChange={setLabourHours} disabled={!editable} />
              </Field>
              <Field label="Labour cost">
                <NumericInput value={labourCost} onChange={setLabourCost} currencyPrefix="Rp" decimalPlaces={0} disabled={!editable} />
              </Field>
            </div>
          </Card>

          <Card padded={false}>
            <div className="flex items-center justify-between p-5 pb-3">
              <div>
                <CardTitle>WO Requisition — required parts</CardTitle>
                <div className="text-caption text-stone mt-0.5">Material need for this work order. Submit for processing to raise the PMR (material request) to supply chain.</div>
              </div>
              <div className="flex items-center gap-2">
                {existing && (existing.requisition_status === 'submitted'
                  ? <StatusPill tone="success" withDot={false}>Submitted</StatusPill>
                  : <StatusPill tone="neutral" withDot={false}>Planned</StatusPill>)}
                {editable && <Button variant="ghost" size="sm" onClick={addLine}><Plus className="size-4" /> Add part</Button>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 w-10">#</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[220px]">Item</th>
                    <th className="text-right font-medium px-3 py-2 w-24">Qty</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">Warehouse</th>
                    <th className="text-right font-medium px-3 py-2 w-44 whitespace-nowrap">Rate</th>
                    <th className="text-right font-medium px-3 py-2 w-40 whitespace-nowrap">Amount</th>
                    {editable && <th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={editable ? 7 : 6} className="px-3 py-6 text-center text-stone">No parts. Add one if the repair consumed spares.</td></tr>
                  )}
                  {lines.map((l, idx) => {
                    const amt = new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'));
                    return (
                      <tr key={l.rowId} className="border-t border-hairline">
                        <td className="px-3 py-2 text-stone">{idx + 1}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox options={itemOptions} value={l.item_id || null} onChange={(v) => pickItem(l.rowId, v)} placeholder="Pick item…" />
                            : <span className="px-3 text-charcoal">{l.item_name || l.item_code || l.item_id}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={l.qty} onChange={(v) => patch(l.rowId, { qty: v })} decimalPlaces={0} />
                            : <span className="num text-right block px-3">{l.qty}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox options={warehouseOptions} value={l.warehouse_id || null} onChange={(v) => patch(l.rowId, { warehouse_id: v ?? '' })} placeholder="— Any —" clearable />
                            : <span className="px-3 text-charcoal">{warehouses.find((w) => w.id === l.warehouse_id)?.name || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={l.rate} onChange={(v) => patch(l.rowId, { rate: v })} currencyPrefix="Rp" decimalPlaces={0} />
                            : <span className="num text-right block px-3 whitespace-nowrap">{money(l.rate)}</span>}
                        </td>
                        <td className="px-3 py-2 text-right num whitespace-nowrap">{money(amt.toString())}</td>
                        {editable && (
                          <td className="px-2 py-1.5">
                            <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => remove(l.rowId)}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-hairline flex flex-wrap items-center justify-end gap-x-4 gap-y-2 num">
              <div className="text-caption text-stone">Parts total</div>
              <div className="font-medium">{money(partsTotal.toString())}</div>
            </div>
          </Card>

          <Card>
            <Field label="Remarks">
              <textarea className="input-base !h-auto !py-2" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!editable} />
            </Field>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card>
            <CardTitle>Summary</CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Type" pill={<StatusPill tone="accent" withDot={false}>{humanizeEnum(woType)}</StatusPill>} />
              {woSubtype && <Row label="Subtype" value={humanizeEnum(woSubtype)} />}
              <Row label="Status" pill={existing ? <WorkOrderStatusPill value={existing.status} /> : <StatusPill tone="neutral">Draft</StatusPill>} />
              <Row label="Requisition" pill={
                (existing?.requisition_status === 'submitted')
                  ? <StatusPill tone="success" withDot={false}>Submitted</StatusPill>
                  : <StatusPill tone="neutral" withDot={false}>Planned</StatusPill>
              } />
              <Row label="Parts" value={String(lines.filter((l) => l.item_id).length)} />
              <Row label="Labour cost" value={existing ? money(existing.labour_cost) : money(labourCost)} />
              <Row label="Parts cost" value={existing ? money(existing.total_parts_cost) : money(partsTotal.toString())} />
              <Row label="Total cost" value={existing ? money(existing.total_cost) : money(totalCost.toString())} highlight />
            </div>
            <div className="mt-2 text-caption text-stone flex items-start gap-1.5">
              <Wrench className="size-3.5 shrink-0 mt-0.5" />
              <span>Complete rolls up parts + labour into total cost, closes the order, and posts the maintenance‑cost journal entry against the contract.</span>
            </div>
          </Card>

          {!isNew && existing && <RelatedDocsPanel doctype="asset_work_order" documentID={existing.id} />}
          {!isNew && existing && <Timeline hideViews doctype="asset_work_order" documentId={existing.id} />}
        </aside>
      </motion.div>
    </>
  );
}

function WorkOrderStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'draft':       return <StatusPill tone="neutral" withDot={false}>Draft</StatusPill>;
    case 'in_progress': return <StatusPill tone="info"    withDot={false}>In progress</StatusPill>;
    case 'completed':   return <StatusPill tone="success" withDot={false}>Completed</StatusPill>;
    case 'cancelled':   return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:            return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

function Row({ label, value, pill, highlight }: { label: string; value?: string; pill?: React.ReactNode; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      {pill ?? <span className={'num text-right ' + (highlight ? 'text-ink font-semibold' : 'text-charcoal')}>{value}</span>}
    </div>
  );
}
